import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FilterToolbarItem } from 'src/components/Filters';
import {
  CityMultiSelector,
  CountryMultiSelector,
  StateProvinceMultiSelector,
} from 'src/components/Selectors/LocationSelector';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ContentId } from 'src/utils/constants/contentId';
import { COLLECTION_FILTER_OP_TO_CID } from 'src/utils/constants/contentIdMaps';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import {
  EntityWithTicketsQuery,
  FilterOperationType,
} from 'src/WebApiController';

export type LocationFilterQuery = Pick<
  EntityWithTicketsQuery,
  'countryCodes' | 'stateProvinceIds' | 'cityIds'
> & {
  operations?: {
    countryCodesOp?: FilterOperationType | null;
    stateProvinceIdsOp?: FilterOperationType | null;
    cityIdsOp?: FilterOperationType | null;
  };
};

export const useLocationFilter = <
  TQuery extends QueryWithViewMode & LocationFilterQuery,
>({
  supportMultiOperation = false,
  isHorizontalLayout = false,
}: {
  supportMultiOperation?: boolean;
  isHorizontalLayout?: boolean;
}): FilterToolbarItem[] => {
  const { tempQuery, setTempQuery } = useFilterQueryContext<TQuery>();

  const { countryCodes, stateProvinceIds, cityIds } = tempQuery;

  return useMemo<FilterToolbarItem[]>(() => {
    return [
      {
        filterId: 'countryCodes',
        labelContentId: ContentId.Country,
        filterQueryKeys: ['countryCodes'],
        filterItem: (
          <CountryMultiSelector
            triggerProps={{ style: { width: '100%' } }}
            key="countryFilter"
            searchable
            values={countryCodes ?? []}
            operation={tempQuery.operations?.countryCodesOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues: newCountryCodes, operation }) => {
              const countryCodesSorted = newCountryCodes.toSorted();
              const orgCountryCodesSorted = (countryCodes ?? []).toSorted();
              const hasChange =
                !isEqual(countryCodesSorted, orgCountryCodesSorted) ||
                operation !== tempQuery.operations?.countryCodesOp;

              if (hasChange) {
                setTempQuery({
                  ...tempQuery,
                  countryCodes: countryCodesSorted?.length
                    ? countryCodesSorted
                    : null,
                  stateProvinceIds: null,
                  cityIds: null,
                  operations: supportMultiOperation
                    ? {
                        ...tempQuery.operations,
                        countryCodesOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
      {
        filterId: 'stateProvinceIds',
        labelContentId: ContentId.StateProvince,
        filterQueryKeys: ['stateProvinceIds'],
        filterItem: (
          <StateProvinceMultiSelector
            triggerProps={{ style: { width: '100%' } }}
            searchable
            key="stateProvinceFilter"
            values={(stateProvinceIds ?? []).map(String)}
            countryCodes={countryCodes}
            operation={tempQuery.operations?.stateProvinceIdsOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues: newStateProvinceIds, operation }) => {
              const stateProvinceIdsSorted = newStateProvinceIds
                .map(Number)
                .toSorted() as number[];
              const orgStateProvinceIdsSorted = (
                stateProvinceIds ?? []
              ).toSorted();
              const hasChange =
                !isEqual(stateProvinceIdsSorted, orgStateProvinceIdsSorted) ||
                operation !== tempQuery.operations?.stateProvinceIdsOp;

              if (hasChange) {
                setTempQuery({
                  ...tempQuery,
                  stateProvinceIds: stateProvinceIdsSorted,
                  cityIds: null,
                  operations: supportMultiOperation
                    ? {
                        ...tempQuery.operations,
                        stateProvinceIdsOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
      {
        filterId: 'cityIds',
        labelContentId: ContentId.City,
        filterQueryKeys: ['cityIds'],
        filterItem: (
          <CityMultiSelector
            triggerProps={{ style: { width: '100%' } }}
            key="cityFilter"
            searchable
            values={(cityIds ?? []).map(String)}
            countryCodes={countryCodes}
            stateProvinceIds={stateProvinceIds ?? []}
            operation={tempQuery.operations?.cityIdsOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues: newCityIds, operation }) => {
              const cityIdsSorted = newCityIds
                .map(Number)
                .toSorted() as number[];
              const orgCityIdsSorted = (cityIds ?? []).toSorted();
              const hasChange =
                !isEqual(cityIdsSorted, orgCityIdsSorted) ||
                operation !== tempQuery.operations?.cityIdsOp;

              if (hasChange) {
                setTempQuery({
                  ...tempQuery,
                  cityIds: cityIdsSorted,
                  operations: supportMultiOperation
                    ? {
                        ...tempQuery.operations,
                        cityIdsOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
    ];
  }, [
    cityIds,
    countryCodes,
    isHorizontalLayout,
    setTempQuery,
    stateProvinceIds,
    supportMultiOperation,
    tempQuery,
  ]);
};
